import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import ArticleListItem from '@/features/editorial/components/article/list/ArticleListItem.vue';
import ArticleListFilter from '@/features/editorial/components/article/list/ArticleListFilter.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';

import type { GetArticleQuery } from '@/features/editorial/graphql/queries/GetArticle.query';
import type { ListArticleData, TitleEdge, TitleListPageInfo } from '@/features/editorial/types/editorial-types';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import { TitleObjectType } from '@/interfaces/titles';
import { TitleOrder } from '@/enums/title-order';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useFollowList } from '@/helpers/composables/useFollowList';
import { addToast } from '@/helpers/vfm/toast-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

type MainObject = AssertByTypename<ListArticleData['mainObjects'][number]['node'], 'GenericTitleList'>;


export default /*#__PURE__*/_defineComponent({
  __name: 'ArticleList',
  props: {
    titles: null,
    pageInfo: null,
    titleOrderMode: { default: 'unranked' },
    listId: null,
    listName: null,
    isFollowed: { type: Boolean },
    listSize: null,
    isLoadingArticles: { type: Boolean },
    fetchMore: null
  },
  setup(__props: any) {

const props = __props as { titles: TitleEdge[], pageInfo: TitleListPageInfo, titleOrderMode: string, listId: string, listName: string, isFollowed: boolean, listSize: number, isLoadingArticles: boolean, fetchMore: (options: {
			variables: { after: string };
			updateQuery: (
				previousResult: GetArticleQuery,
				options: { fetchMoreResult?: GetArticleQuery }
			) => GetArticleQuery;
		}) => Promise<unknown> | undefined };



const filterStore = useFilterStore();

const { tc } = useI18n();

const activeContentType = ref<TitleObjectType | null>(null);

const hasMixedObjectTypes = computed(() => {
	const types = new Set(props.titles.map(t => t.node.objectType));
	return types.size > 1;
});

function toggleContentType(value: TitleObjectType) {
	activeContentType.value = activeContentType.value === value ? null : value;
}

const { toggleFollowList } = useFollowList();

function onAddTitles() {
	toggleFollowList({
		listId: props.listId,
		isFollowed: props.isFollowed,
		listName: props.listName,
	});
}

const isFilterActive = computed(
	() => activeContentType.value !== null || (filterStore.currentFilters?.providers?.length ?? 0) > 0
);

const filteredTitles = computed(() => {
	let result = props.titles;

	if (activeContentType.value !== null) {
		result = result.filter(t => t.node.objectType.toLowerCase() === activeContentType.value);
	}

	const activeProviders = filterStore.currentFilters?.providers ?? [];
	if (activeProviders.length > 0) {
		result = result.filter(t => t.node.offers?.some(o => activeProviders.includes(o.package.shortName)));
	}

	const sortBy = filterStore.currentFilters?.sort_by ?? null;
	return sortTitles(result, sortBy);
});

function sortTitles(titles: TitleEdge[], sortBy: TitleOrder | null): TitleEdge[] {
	if (!sortBy || sortBy === TitleOrder.POPULARITY) return titles;

	switch (sortBy) {
		case TitleOrder.ALPHABETICAL:
			return titles.toSorted((a, b) => a.node.content.title.localeCompare(b.node.content.title));
		case TitleOrder.RELEASE_YEAR:
			return titles.toSorted(
				(a, b) => (b.node.content.originalReleaseYear ?? 0) - (a.node.content.originalReleaseYear ?? 0)
			);
		case TitleOrder.SCORE_IMDB:
			return titles.toSorted(
				(a, b) => (b.node.content.scoring?.imdbScore ?? 0) - (a.node.content.scoring?.imdbScore ?? 0)
			);
		case TitleOrder.TMDB_POPULARITY:
			return titles.toSorted(
				(a, b) => (b.node.content.scoring?.tmdbPopularity ?? 0) - (a.node.content.scoring?.tmdbPopularity ?? 0)
			);
		default:
			return titles;
	}
}

function onShareList() {
	if (navigator && navigator.clipboard) {
		navigator.clipboard.writeText(window.location.href);
		addToast({ message: tc('WEBAPP_COPIED_TO_CLIPBOARD') });

		TrackingHelper.trackEvent('userinteraction', {
			action: 'share_content',
			label: 'list',
			property: props.listId,
		});
	}
}

const isAtEndOfList = computed(() => !props.pageInfo.hasNextPage);

async function onLoadNext({ event }: { event: { target?: { complete: (len: number) => void } } }) {
	if (!event.target || props.isLoadingArticles) return;

	const endCursor = props.pageInfo.endCursor;
	if (!endCursor) return;

	try {
		await props.fetchMore({
			variables: { after: endCursor },
			updateQuery(previousResult, { fetchMoreResult }) {
				if (!fetchMoreResult) return previousResult;

				const previousMainObject = (previousResult.urlV2.node as ListArticleData).mainObjects[0]
					.node as MainObject;
				const fetchedTitles = (
					(fetchMoreResult.urlV2.node as ListArticleData).mainObjects[0].node as MainObject
				).titles;

				if (previousMainObject.titles.edges == null || fetchedTitles.edges == null) return previousResult;

				previousMainObject.titles.edges.push(...fetchedTitles.edges);
				previousMainObject.titles.pageInfo = fetchedTitles.pageInfo;

				return previousResult;
			},
		});
	} catch (error: any) {
		captureMessageForSentry(
			'[GraphQL List Article Page Load More Titles Issue]:',
			{ error, where: 'Component: ArticleList.vue, method: onLoadNext' },
			'error'
		);
	} finally {
		event.target?.complete(props.titles.length);
	}
}

return { __sfc: true,props, filterStore, tc, activeContentType, hasMixedObjectTypes, toggleContentType, toggleFollowList, onAddTitles, isFilterActive, filteredTitles, sortTitles, onShareList, isAtEndOfList, onLoadNext, ArticleListItem, ArticleListFilter, InfiniteScroll }
}

})